/**
 * OralSelfExamCard — самоизследване на устната кухина (орален рак).
 * Воден оглед по последователността на WHO, находки, рискови фактори и
 * дневник на лезии със снимки и правилото на трите седмици.
 * Данни: /api/dental-prevention/self-exam · /lesion (+ /photo, /resolve)
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ScanFace, CheckCircle2, Circle, Camera, Trash2, Loader2, AlertTriangle,
  CalendarClock,
} from "lucide-react";
import { useAuth } from "@/App";

const KINDS = [
  ["white_patch", "бяло петно"], ["red_patch", "червено петно"],
  ["ulcer", "язва или рана"], ["lump", "подутина или втвърдяване"],
  ["node", "възел на шията"], ["other", "друго"],
];

function LesionPhoto({ api, lesionId, photo }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true, created = null;
    (async () => {
      try {
        const res = await axios.get(
          `${api}/dental-prevention/lesion/${lesionId}/photo/${photo.photo_id}`,
          { responseType: "blob" });
        created = URL.createObjectURL(res.data);
        if (alive) setUrl(created); else URL.revokeObjectURL(created);
      } catch { /* снимката не се зареди */ }
    })();
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [api, lesionId, photo.photo_id]);
  return (
    <div className="shrink-0 text-center" data-testid={`lesion-photo-${photo.photo_id}`}>
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-500/20 flex items-center justify-center">
        {url ? <img src={url} alt="лезия" className="w-full h-full object-cover" />
             : <Loader2 className="w-4 h-4 animate-spin opacity-50" />}
      </div>
      <div className="text-[9px] opacity-60 mt-0.5">{photo.taken_on}</div>
    </div>
  );
}

export default function OralSelfExamCard({ isDark = false, cardCls = "" }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [steps, setSteps] = useState([]);
  const [findings, setFindings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-prevention/self-exam`);
      setD(data);
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";
  const inputCls = `px-2 py-1 rounded-lg border text-[12px] ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  const toggle = (arr, set, key) =>
    set(arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]);

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/dental-prevention/self-exam`, {
        findings, steps_done: steps,
      });
      setD(data); setSteps([]); setFindings([]);
      toast.success(findings.length ? "Огледът е записан — заведете лезия за проследяване"
                                    : "Огледът е записан — без находки");
    } catch { toast.error("Не се записа"); }
    finally { setBusy(false); }
  };

  const saveRisk = async (keys) => {
    try {
      const { data } = await axios.post(`${API}/dental-prevention/self-exam/risk`,
        { factors: keys });
      setD(data);
    } catch { toast.error("Не се записа"); }
  };

  const saveLesion = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/dental-prevention/lesion`, {
        site: form.site, kind: form.kind, first_seen: form.first_seen,
        size_mm: form.size_mm === "" ? null : Number(form.size_mm),
        painful: !!form.painful, note: form.note,
      });
      setD(data); setForm(null); toast.success("Лезията се проследява");
    } catch (e) { toast.error(e?.response?.data?.detail || "Не се записа"); }
    finally { setBusy(false); }
  };

  const upload = async (lid, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const { data } = await axios.post(
        `${API}/dental-prevention/lesion/${lid}/photo`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setD(data); toast.success("Снимката е добавена");
    } catch (e) { toast.error(e?.response?.data?.detail || "Снимката не се качи"); }
    finally { setBusy(false); }
  };

  const act = async (url, msg) => {
    try {
      const { data } = await axios.post(`${API}${url}`);
      setD(data); toast.success(msg);
    } catch { toast.error("Неуспешно"); }
  };

  const removeLesion = async (lid) => {
    try {
      const { data } = await axios.delete(`${API}/dental-prevention/lesion/${lid}`);
      setD(data); toast.success("Изтрито");
    } catch { toast.error("Неуспешно"); }
  };

  const siteLabel = (key) =>
    d.steps.find((s) => s.key === key)?.title_bg || key;

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-selfexam-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <ScanFace className="w-4 h-4 text-cyan-500" /> Самоизследване на устната кухина
          </div>
          <p className="text-[11px] opacity-70 mt-0.5">{d.intro_bg}</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            d.risk_level_bg === "висок" ? "bg-rose-500/20 text-rose-600"
              : d.risk_level_bg === "повишен" ? "bg-amber-500/20 text-amber-600"
              : "bg-emerald-500/20 text-emerald-600"}`}
            data-testid="selfexam-risk-level">
            риск: {d.risk_level_bg}
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-600"
            data-testid="selfexam-count">
            {d.exam_count} огледа
          </span>
        </div>
      </div>

      <div className="mt-2 p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30"
        data-testid="selfexam-three-week">
        <div className="text-[10px] opacity-90">{d.three_week_rule_bg}</div>
      </div>

      {d.alerts?.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid="selfexam-alerts">
          {d.alerts.map((a, i) => (
            <div key={i} className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/30">
              <div className="text-[11px] font-bold text-rose-500 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {a.title_bg}
              </div>
              <div className="text-[10px] opacity-85 mt-0.5">{a.text_bg}</div>
            </div>
          ))}
        </div>
      )}

      {/* стъпки */}
      <div className="mt-3 space-y-1.5" data-testid="selfexam-steps">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">
            Осем стъпки по реда на WHO
          </div>
          <div className="text-[10px] opacity-60 inline-flex items-center gap-1">
            <CalendarClock className="w-3 h-3" />
            {d.last_exam ? `последно преди ${d.last_exam_days} дни` : "още няма оглед"}
          </div>
        </div>
        {d.steps.map((s, i) => (
          <div key={s.key} className={`p-2 rounded-lg border ${border} flex items-start gap-2`}
            data-testid={`selfexam-step-${s.key}`}>
            <button onClick={() => toggle(steps, setSteps, s.key)} className="mt-0.5"
              data-testid={`selfexam-step-toggle-${s.key}`}>
              {steps.includes(s.key)
                ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                : <Circle className="w-4 h-4 opacity-50" />}
            </button>
            <div className="min-w-0">
              <div className="text-[11px] font-bold">{i + 1}. {s.title_bg}</div>
              <div className="text-[10px] opacity-75">{s.how_bg}</div>
              <div className="text-[10px] opacity-60 italic">Търсите: {s.look_bg}</div>
            </div>
          </div>
        ))}
      </div>

      {/* находки */}
      <div className="mt-3" data-testid="selfexam-findings">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1">
          Намерихте ли нещо от това?
        </div>
        <div className="flex flex-wrap gap-1.5">
          {d.findings.map((f) => (
            <button key={f.key} onClick={() => toggle(findings, setFindings, f.key)}
              className={`px-2 py-1 rounded-full text-[10px] font-bold border transition ${
                findings.includes(f.key) ? "border-rose-500 bg-rose-500/15 text-rose-500"
                                         : `${border} opacity-70`}`}
              data-testid={`selfexam-finding-${f.key}`}>
              {f.label_bg}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <button onClick={complete} disabled={busy || !steps.length}
            className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-cyan-500 to-sky-600 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="selfexam-complete">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Запиши огледа ({steps.length}/{d.steps.length})
          </button>
          <button onClick={() => setForm({
            site: d.steps[0].key, kind: "ulcer",
            first_seen: new Date().toISOString().slice(0, 10),
            size_mm: "", painful: false, note: "",
          })}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border ${border}`}
            data-testid="selfexam-add-lesion">
            Заведи лезия за 21 дни
          </button>
        </div>
        <div className="text-[10px] opacity-70 mt-1.5">{d.cta_bg}</div>
      </div>

      {/* нова лезия */}
      {form && (
        <div className={`mt-3 p-3 rounded-xl border ${border} space-y-2`} data-testid="lesion-form">
          <div className="flex flex-wrap gap-2">
            <select value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })}
              className={inputCls} data-testid="lesion-site">
              {d.steps.map((s) => <option key={s.key} value={s.key}>{s.title_bg}</option>)}
            </select>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className={inputCls} data-testid="lesion-kind">
              {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input type="date" value={form.first_seen}
              onChange={(e) => setForm({ ...form, first_seen: e.target.value })}
              className={inputCls} data-testid="lesion-first-seen" />
            <input type="number" step="0.5" placeholder="размер мм" value={form.size_mm}
              onChange={(e) => setForm({ ...form, size_mm: e.target.value })}
              className={`${inputCls} w-24`} data-testid="lesion-size" />
          </div>
          <label className="flex items-center gap-2 text-[10px] opacity-80">
            <input type="checkbox" checked={form.painful}
              onChange={(e) => setForm({ ...form, painful: e.target.checked })}
              data-testid="lesion-painful" /> Боли
          </label>
          <input placeholder="Забележка (как изглежда, какво го дразни)" value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className={`${inputCls} w-full`} data-testid="lesion-note" />
          <div className="flex gap-2">
            <button onClick={saveLesion} disabled={busy}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-cyan-500 to-sky-600 disabled:opacity-60"
              data-testid="lesion-save">Запази</button>
            <button onClick={() => setForm(null)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border ${border}`}
              data-testid="lesion-cancel">Отказ</button>
          </div>
        </div>
      )}

      {/* лезии */}
      {d.lesions?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="selfexam-lesions">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">
            Проследявани промени
          </div>
          {d.lesions.map((l) => (
            <div key={l.lesion_id}
              className={`p-2.5 rounded-xl border ${l.urgent ? "border-rose-500/50 bg-rose-500/5" : border}`}
              data-testid={`lesion-${l.lesion_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] font-bold">
                    {siteLabel(l.site)} · {(KINDS.find((k) => k[0] === l.kind) || [, l.kind])[1]}
                  </div>
                  <div className="text-[10px] opacity-75">
                    от {l.first_seen}
                    {l.days_open != null && !l.resolved ? ` · ${l.days_open} дни` : ""}
                    {l.size_mm ? ` · ${l.size_mm} мм` : ""}
                    {l.painful ? " · боли" : ""}
                    {l.resolved ? " · отшумяла" : ""}
                  </div>
                  {l.note && <div className="text-[10px] opacity-60 mt-0.5">{l.note}</div>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <label className="cursor-pointer p-1.5 rounded-lg bg-cyan-500/15 text-cyan-600"
                    data-testid={`lesion-upload-${l.lesion_id}`}>
                    <Camera className="w-3.5 h-3.5" />
                    <input type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={(e) => upload(l.lesion_id, e.target.files?.[0])} />
                  </label>
                  {!l.resolved && (
                    <button onClick={() => act(`/dental-prevention/lesion/${l.lesion_id}/resolve`, "Отбелязана като отшумяла")}
                      className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-600"
                      data-testid={`lesion-resolve-${l.lesion_id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => removeLesion(l.lesion_id)}
                    className="p-1.5 rounded-lg bg-rose-500/10 text-rose-500"
                    data-testid={`lesion-delete-${l.lesion_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {l.photos?.length > 0 && (
                <div className="flex gap-2 mt-2 overflow-x-auto">
                  {l.photos.map((p) => (
                    <LesionPhoto key={p.photo_id} api={API} lesionId={l.lesion_id} photo={p} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* рискови фактори */}
      <div className="mt-3" data-testid="selfexam-risk">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1">
          Рискови фактори · препоръчан професионален оглед на {d.professional_interval_months} месеца
        </div>
        <div className="flex flex-wrap gap-1.5">
          {d.risk_factors.map((f) => {
            const on = d.risk_selected.includes(f.key);
            return (
              <button key={f.key}
                onClick={() => saveRisk(on
                  ? d.risk_selected.filter((k) => k !== f.key)
                  : [...d.risk_selected, f.key])}
                className={`px-2 py-1 rounded-full text-[10px] font-bold border transition ${
                  on ? "border-rose-500 bg-rose-500/15 text-rose-500" : `${border} opacity-70`}`}
                data-testid={`selfexam-risk-${f.key}`}>
                {f.label_bg}
              </button>
            );
          })}
        </div>
      </div>

      <div className="text-[9px] opacity-50 mt-2">{d.note_bg}</div>
    </div>
  );
}
